import { useState, useEffect } from 'react';
import { useConfig } from '../context/ConfigContext';
import { describeBandDays, WEEKDAYS } from '../utils/bookingUtils';
import {
  getPackages,
  getPackageWindowHours,
  getPackageBufferMinutes,
  describePackageWindow,
  formatPackageHour,
  getLatestPackageStartHour,
  getOptionRates,
  normalizePackageOptions,
  getPackageRateBands,
  describePackageBandHours,
} from '../utils/packageUtils';
import { validatePackage, ISSUE_LEVELS } from '../utils/packageValidationUtil';
import { formatMoney, getCurrencySymbol, parsePriceInput } from '../utils/currencyUtil';
import Modal from './Modal';
import { ADMIN_COLORS } from '../constants/bookingConst';

const COLORS = ADMIN_COLORS;

// Same three-row card rhythm as the court cards in Config -> Sports &
// Courts: a package has too many fields to sit on one wrapping line, and
// grouping them by purpose keeps each row readable however many options or
// inclusions are added.
const ROW = { display: 'flex', alignItems: 'flex-start', gap: '0.6rem', flexWrap: 'wrap' };
const SUBSECTION = { borderTop: `1px dashed ${COLORS.line}`, paddingTop: '0.55rem', marginTop: '0.1rem' };

const newId = (prefix) => `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

// A day toggle identical to the rate-band pills, so an admin who has set up
// weekday/weekend hourly rates already knows how this works. Empty = every
// day (bandAppliesOnDay's convention), which is why clearing the last pill
// is allowed rather than blocked.
function DayPills({ days, onToggle }) {
  const on = (index) => Array.isArray(days) && days.map(Number).includes(index);
  return (
    <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.15rem' }}>
      <span style={{ fontSize: '0.66rem', color: COLORS.textSecondary, marginRight: '0.15rem' }}>Applies on</span>
      {WEEKDAYS.map((day) => {
        const active = on(day.index);
        return (
          <button
            key={day.index}
            type="button"
            onClick={() => onToggle(day.index)}
            aria-pressed={active}
            title={day.label}
            style={{
              padding: '0.15rem 0.4rem', borderRadius: '999px', cursor: 'pointer',
              fontSize: '0.66rem', lineHeight: 1.5,
              border: `1px solid ${active ? COLORS.cyanDeep : COLORS.line}`,
              background: active ? COLORS.cyanDeep : 'transparent',
              color: active ? '#ffffff' : COLORS.textSecondary,
            }}
          >
            {day.short}
          </button>
        );
      })}
      <span style={{ fontSize: '0.66rem', opacity: 0.7, marginLeft: '0.2rem' }}>{describeBandDays({ days })}</span>
    </div>
  );
}

// Event packages — whole-court bookings sold as a block of hours rather than
// by the hour. Authoring only: this screen defines what a club offers and
// checks it can actually be booked. The guest-facing booking flow reads the
// same config (see packageUtils.js).
function AdminPackages() {
  const { config, loading: configLoading, updateConfig } = useConfig();

  // A local draft, saved explicitly — the same model as Config, and for the
  // same reason: a package is a dozen related fields that are only coherent
  // once they're all filled in, so writing on every keystroke would publish
  // half-built packages to guests.
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalConfig, setModalConfig] = useState({ isOpen: false });

  const showModal = (cfg) => setModalConfig({ isOpen: true, ...cfg });
  const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));

  // Seeded ONCE, deliberately. Re-syncing whenever `config` changes would
  // wipe an in-progress package every time anything else wrote to the config
  // node — which is exactly the failure the Config screen already has.
  useEffect(() => {
    if (configLoading || !config || draft !== null) return;
    setDraft({
      // Normalized on the way in so a package authored before rates were
      // nested — a separate card per weekday price — opens as one card per
      // duration with its prices stacked inside, instead of as duplicate
      // durations the admin would have to merge by hand.
      packages: getPackages(config).map(p => ({ ...p, options: normalizePackageOptions(p.options, newId) })),
      packageBufferMinutes: config.packageBufferMinutes ?? 60,
    });
  }, [configLoading, config, draft]);

  if (configLoading || draft === null) {
    return <p style={{ opacity: 0.6, fontSize: '0.85rem' }}>Loading packages…</p>;
  }

  const packages = draft.packages;
  const setPackages = (next) => {
    setDraft(prev => ({ ...prev, packages: next }));
    setDirty(true);
  };
  const updatePackage = (id, patch) =>
    setPackages(packages.map(p => (p.id === id ? { ...p, ...patch } : p)));

  const handleAddPackage = () => {
    setPackages([...packages, {
      id: newId('pkg'),
      name: '',
      description: '',
      options: [{ id: newId('opt'), hours: 4, price: '', days: [] }],
      courts: [],
      // 3PM-12AM: the example from the spec, and a sane default for an
      // evening events package.
      startHour: 15,
      endHour: 0,
      bufferMinutes: null,
      inclusions: [{ id: newId('inc'), text: '' }],
      active: true,
    }]);
  };

  const handleRemovePackage = (pkg) => {
    showModal({
      title: 'Remove Package',
      message: `Remove "${(pkg.name || '').trim() || 'this package'}"? Guests will no longer be able to book it.`
        + '\n\nTo hide it temporarily instead, untick Active.',
      type: 'warning',
      confirmText: 'Remove',
      showCancel: true,
      cancelText: 'Cancel',
      onConfirm: () => {
        setPackages(packages.filter(p => p.id !== pkg.id));
        closeModal();
      },
    });
  };

  // ---- Options (one duration) and their rates (price + days) ----
  // An option is a duration the club sells; the prices hang off it, because
  // "4 hours" is one thing to a guest whether it costs ₱1,500 or ₱2,000 that
  // day. See getOptionRates in packageUtils for the stored shape.
  const updateOption = (pkgId, optId, field, value) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, {
      options: (pkg.options || []).map(o => (o.id === optId ? { ...o, [field]: value } : o)),
    });
  };
  const addOption = (pkgId) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, {
      options: [...(pkg.options || []), { id: newId('opt'), hours: '', rates: [{ id: newId('rate'), price: '', days: [] }] }],
    });
  };
  const removeOption = (pkgId, optId) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, { options: (pkg.options || []).filter(o => o.id !== optId) });
  };

  // Rate edits always write the WHOLE rates array back, so an option still
  // carrying the legacy flat price/days is upgraded the first time it's
  // touched rather than ending up with both shapes at once.
  const patchRates = (pkgId, optId, mapRates) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, {
      options: (pkg.options || []).map((o) => {
        if (o.id !== optId) return o;
        const { price, days, ...rest } = o;
        return { ...rest, rates: mapRates(getOptionRates(o)) };
      }),
    });
  };
  const updateRate = (pkgId, optId, rateId, field, value) =>
    patchRates(pkgId, optId, rates => rates.map(r => (r.id === rateId ? { ...r, [field]: value } : r)));
  const toggleRateDay = (pkgId, optId, rateId, dayIndex) =>
    patchRates(pkgId, optId, rates => rates.map((r) => {
      if (r.id !== rateId) return r;
      const days = Array.isArray(r.days) ? r.days.map(Number) : [];
      return { ...r, days: days.includes(dayIndex) ? days.filter(d => d !== dayIndex) : [...days, dayIndex].sort() };
    }));
  const addRate = (pkgId, optId) =>
    patchRates(pkgId, optId, (rates) => {
      // A second rate defaults to the days the existing ones DON'T cover, so
      // adding a weekend price to a weekday one needs no extra clicking — and
      // never lands in the overlap error by default.
      const taken = new Set(rates.flatMap(r => (Array.isArray(r.days) && r.days.length ? r.days.map(Number) : [0, 1, 2, 3, 4, 5, 6])));
      const free = [0, 1, 2, 3, 4, 5, 6].filter(d => !taken.has(d));
      return [...rates, { id: newId('rate'), price: '', days: free }];
    });
  const removeRate = (pkgId, optId, rateId) =>
    patchRates(pkgId, optId, rates => rates.filter(r => r.id !== rateId));

  // ---- Hourly rates ----
  // The alternative to preset options: a package with no fixed durations is
  // priced by the hour, and the guest picks any start and end inside the
  // window. Scoped by time range AND day, exactly like an option's rates, so
  // an evening or weekend premium is a second band rather than a second
  // package.
  const updateBand = (pkgId, bandId, field, value) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, {
      rateBands: getPackageRateBands(pkg).map(b => (b.id === bandId ? { ...b, [field]: value } : b)),
    });
  };
  const toggleBandDay = (pkgId, bandId, dayIndex) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, {
      rateBands: getPackageRateBands(pkg).map((b) => {
        if (b.id !== bandId) return b;
        const days = Array.isArray(b.days) ? b.days.map(Number) : [];
        return { ...b, days: days.includes(dayIndex) ? days.filter(d => d !== dayIndex) : [...days, dayIndex].sort() };
      }),
    });
  };
  const addBand = (pkgId) => {
    const pkg = packages.find(p => p.id === pkgId);
    const bands = getPackageRateBands(pkg);
    // A first band defaults to the package's own window, which is the answer
    // for a club with one flat hourly rate — nothing else to fill in.
    updatePackage(pkgId, {
      rateBands: [...bands, {
        id: newId('band'),
        startHour: bands.length === 0 ? (pkg.startHour ?? 0) : (pkg.endHour ?? 0),
        endHour: pkg.endHour ?? 0,
        price: '',
        days: [],
      }],
    });
  };
  const removeBand = (pkgId, bandId) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, { rateBands: getPackageRateBands(pkg).filter(b => b.id !== bandId) });
  };

  // ---- Courts ----
  const toggleCourt = (pkgId, courtNumber) => {
    const pkg = packages.find(p => p.id === pkgId);
    const courts = (pkg.courts || []).map(Number);
    updatePackage(pkgId, {
      courts: courts.includes(courtNumber)
        ? courts.filter(c => c !== courtNumber)
        : [...courts, courtNumber].sort((a, b) => a - b),
    });
  };

  // ---- Inclusions ----
  const updateInclusion = (pkgId, incId, text) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, { inclusions: (pkg.inclusions || []).map(i => (i.id === incId ? { ...i, text } : i)) });
  };
  const addInclusion = (pkgId) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, { inclusions: [...(pkg.inclusions || []), { id: newId('inc'), text: '' }] });
  };
  const removeInclusion = (pkgId, incId) => {
    const pkg = packages.find(p => p.id === pkgId);
    updatePackage(pkgId, { inclusions: (pkg.inclusions || []).filter(i => i.id !== incId) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Blank inclusion rows are an editing artifact, not content — drop
      // them on save so the guest side never has to filter empties. Options
      // are normalized again here so anything still holding the legacy flat
      // price/days is written back in the nested-rates shape.
      const cleaned = packages.map(p => ({
        ...p,
        options: normalizePackageOptions(p.options, newId),
        inclusions: (p.inclusions || []).filter(i => (i.text || '').trim()),
      }));
      await updateConfig({
        packages: cleaned,
        packageBufferMinutes: Number(draft.packageBufferMinutes) || 0,
      });
      setDraft(prev => ({ ...prev, packages: cleaned }));
      setDirty(false);
      showModal({ title: 'Saved', message: 'Packages saved successfully.', type: 'success', confirmText: 'OK', onConfirm: closeModal });
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to save packages: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setSaving(false);
  };

  const allCourts = (config?.courts || []).slice().sort((a, b) => Number(a.number) - Number(b.number));

  return (
    <div>
      <Modal {...modalConfig} onClose={closeModal} />

      <h3 style={{ color: COLORS.ink, marginTop: 0, marginBottom: '0.35rem' }}>Packages</h3>
      <p style={{ opacity: 0.65, fontSize: '0.8rem', margin: '0 0 1rem' }}>
        Whole-court bookings — birthdays, corporate nights, tournaments. Each package sets which courts it takes
        over and the window guests may book it in, then prices it one of two ways: preset durations
        (&quot;4 hours, {formatMoney(1500, config)}&quot;), or hourly rates guests book any start and end time against.
      </p>

      {/* Club-wide turnover default. Lives here rather than in Config so it
          sits next to the per-package overrides it feeds. */}
      <div className="form-group" style={{ maxWidth: '340px', marginBottom: '1rem' }}>
        <label style={{ fontSize: '0.78rem' }}>Default cleanup allowance (minutes)</label>
        <input
          type="number"
          min="0"
          value={draft.packageBufferMinutes ?? ''}
          onChange={(e) => { setDraft(prev => ({ ...prev, packageBufferMinutes: e.target.value })); setDirty(true); }}
          style={{ width: '110px' }}
        />
        <small style={{ display: 'block', marginTop: '0.25rem', opacity: 0.6, fontSize: '0.72rem' }}>
          Gap held after every package booking so staff can clear and reset the courts before the next one.
          Any package can override it below.
        </small>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '0.75rem' }}>
        {packages.map((pkg) => {
          const issues = validatePackage(pkg, config);
          const windowHours = getPackageWindowHours(pkg);
          const bands = getPackageRateBands(pkg);
          const resolvedBuffer = getPackageBufferMinutes(
            { packageBufferMinutes: Number(draft.packageBufferMinutes) || 0 }, pkg
          );
          return (
            <div
              key={pkg.id}
              style={{
                display: 'flex', flexDirection: 'column', gap: '0.55rem',
                background: COLORS.cardBg, padding: '0.75rem 0.85rem', borderRadius: '10px',
                border: `1px solid ${issues.some(i => i.level === ISSUE_LEVELS.ERROR) ? 'rgba(192,57,43,.45)' : COLORS.line}`,
                opacity: pkg.active === false ? 0.72 : 1,
              }}
            >
              {/* Row 1 — what the package IS. */}
              <div style={ROW}>
                <div className="form-group" style={{ margin: 0, flex: '1 1 220px' }}>
                  <label style={{ fontSize: '0.7rem' }}>Package name</label>
                  <input
                    type="text"
                    value={pkg.name || ''}
                    onChange={(e) => updatePackage(pkg.id, { name: e.target.value })}
                    placeholder="e.g. Birthday Package"
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '0.7rem' }}>Active</label>
                  {/* Hides the package from guests without deleting it and
                      losing its options — the off-season switch. */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.72rem', cursor: 'pointer', paddingTop: '0.35rem', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={pkg.active !== false}
                      onChange={(e) => updatePackage(pkg.id, { active: e.target.checked })}
                    />
                    <span>{pkg.active !== false ? 'Offered' : 'Hidden'}</span>
                  </label>
                </div>
                <button
                  onClick={() => handleRemovePackage(pkg)}
                  style={{ marginLeft: 'auto', alignSelf: 'center', background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '0.9rem' }}
                  aria-label="Remove package"
                >
                  ✕
                </button>
                <div className="form-group" style={{ margin: 0, flexBasis: '100%' }}>
                  <label style={{ fontSize: '0.7rem' }}>Description</label>
                  <textarea
                    rows={2}
                    value={pkg.description || ''}
                    onChange={(e) => updatePackage(pkg.id, { description: e.target.value })}
                    placeholder="What this package is for, who it suits, anything guests should know before booking."
                    style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>
              </div>

              {/* Row 2 — durations and prices. One card per DURATION, with a
                  rate line per day range inside it: "4 hours" is one thing a
                  guest buys, whether it costs ₱1,500 on a Tuesday or ₱2,000
                  on a Saturday. Two cards for the same hours would instead be
                  two competing offers, and only the first would ever sell. */}
              <div style={SUBSECTION}>
                <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.35rem' }}>Package options</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {(pkg.options || []).map((opt) => {
                    const hours = Number(opt.hours) || 0;
                    const latest = getLatestPackageStartHour(pkg, hours);
                    const rates = getOptionRates(opt);
                    return (
                      <div
                        key={opt.id}
                        style={{
                          display: 'flex', flexDirection: 'column', gap: '0.4rem',
                          padding: '0.5rem 0.6rem', borderRadius: '6px',
                          border: `1px solid ${COLORS.line}`, background: COLORS.surface,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '0.66rem' }}>Hours</label>
                            <input
                              type="number" min="1" max="24"
                              value={opt.hours ?? ''}
                              onChange={(e) => updateOption(pkg.id, opt.id, 'hours', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
                              style={{ width: '75px' }}
                            />
                          </div>
                          {/* The admin's real question is "when can someone
                              actually start this?" — answered here instead of
                              leaving them to do the arithmetic. */}
                          <span style={{ fontSize: '0.7rem', opacity: 0.75, paddingBottom: '0.35rem' }}>
                            {hours > 0 && latest !== null
                              ? `Can start ${formatPackageHour(pkg.startHour)} – ${formatPackageHour(latest)}`
                              : hours > 0 ? "Doesn't fit the time range" : ''}
                          </span>
                          <button
                            onClick={() => removeOption(pkg.id, opt.id)}
                            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '0.8rem', paddingBottom: '0.35rem' }}
                            aria-label="Remove option"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Rates for this duration. Indented under the hours
                            so the nesting is readable at a glance. */}
                        <div style={{ borderLeft: `2px solid ${COLORS.line}`, paddingLeft: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                          <span style={{ fontSize: '0.66rem', color: COLORS.textSecondary }}>
                            {rates.length > 1 ? `Rates (${rates.length})` : 'Rate'}
                          </span>
                          {rates.map(rate => (
                            <div key={rate.id} style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label style={{ fontSize: '0.66rem' }}>Price ({getCurrencySymbol(config)})</label>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={rate.price ?? ''}
                                  onChange={(e) => updateRate(pkg.id, opt.id, rate.id, 'price', e.target.value === '' ? '' : parsePriceInput(e.target.value))}
                                  style={{ width: '110px' }}
                                />
                              </div>
                              <div style={{ flex: '1 1 260px', paddingBottom: '0.15rem' }}>
                                <DayPills days={rate.days} onToggle={(d) => toggleRateDay(pkg.id, opt.id, rate.id, d)} />
                              </div>
                              {/* The last rate stays: an option with no rate
                                  at all can never be quoted, so removing it
                                  means removing the option. */}
                              {rates.length > 1 && (
                                <button
                                  onClick={() => removeRate(pkg.id, opt.id, rate.id)}
                                  style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '0.8rem', paddingBottom: '0.35rem' }}
                                  aria-label="Remove rate"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => addRate(pkg.id, opt.id)}
                            style={{ alignSelf: 'flex-start', padding: '0.25rem 0.6rem', background: 'none', border: `1px dashed ${COLORS.line}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem', color: COLORS.textSecondary }}
                          >
                            + Add rate
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => addOption(pkg.id)}
                  style={{ marginTop: '0.4rem', padding: '0.35rem 0.7rem', background: 'none', border: `1px dashed ${COLORS.line}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.74rem', color: COLORS.textSecondary }}
                >
                  + Add option
                </button>
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.7rem', opacity: 0.6 }}>
                  One option per duration. Charging a different price on weekends is a second rate inside the
                  same option, not a second option. Leave this empty to price the package by the hour instead.
                </p>
              </div>

              {/* Hourly rates — the other way to price a package. Shown only
                  when there are no preset durations (or when bands already
                  exist), because a package sells one way or the other and
                  offering both editors at once invites configuring both and
                  wondering which the guest gets. */}
              {((pkg.options || []).length === 0 || bands.length > 0) && (
                <div style={SUBSECTION}>
                  <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.15rem' }}>Hourly rates</label>
                  <p style={{ margin: '0 0 0.4rem', fontSize: '0.7rem', opacity: 0.6 }}>
                    With no package options above, guests pick any start and end time inside the window and pay
                    by the hour. One band per time range; add another for a different price on other days.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {bands.map(band => (
                      <div
                        key={band.id}
                        style={{
                          display: 'flex', alignItems: 'flex-end', gap: '0.5rem', flexWrap: 'wrap',
                          padding: '0.45rem 0.6rem', borderRadius: '6px',
                          border: `1px solid ${COLORS.line}`, background: COLORS.surface,
                        }}
                      >
                        <div className="form-group" style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.66rem' }}>From (24h)</label>
                          <input
                            type="number" min="0" max="23"
                            value={band.startHour ?? ''}
                            onChange={(e) => updateBand(pkg.id, band.id, 'startHour', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
                            style={{ width: '80px' }}
                          />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.66rem' }}>To (24h)</label>
                          <input
                            type="number" min="0" max="23"
                            value={band.endHour ?? ''}
                            onChange={(e) => updateBand(pkg.id, band.id, 'endHour', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
                            style={{ width: '80px' }}
                          />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.66rem' }}>Price ({getCurrencySymbol(config)}/hr)</label>
                          <input
                            type="number" min="0" step="0.01"
                            value={band.price ?? ''}
                            onChange={(e) => updateBand(pkg.id, band.id, 'price', e.target.value === '' ? '' : parsePriceInput(e.target.value))}
                            style={{ width: '110px' }}
                          />
                        </div>
                        {/* "17 to 5" is not readable as an evening rate. */}
                        <span style={{ fontSize: '0.7rem', opacity: 0.75, paddingBottom: '0.35rem' }}>
                          {describePackageBandHours(band)}
                          {Number(band.endHour) <= Number(band.startHour) && Number(band.endHour) !== Number(band.startHour)
                            ? ' — ends next day' : ''}
                        </span>
                        <button
                          onClick={() => removeBand(pkg.id, band.id)}
                          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '0.8rem', paddingBottom: '0.35rem' }}
                          aria-label="Remove hourly rate"
                        >
                          ✕
                        </button>
                        <DayPills days={band.days} onToggle={(d) => toggleBandDay(pkg.id, band.id, d)} />
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => addBand(pkg.id)}
                    style={{ marginTop: '0.4rem', padding: '0.35rem 0.7rem', background: 'none', border: `1px dashed ${COLORS.line}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.74rem', color: COLORS.textSecondary }}
                  >
                    + Add hourly rate
                  </button>
                </div>
              )}

              {/* Row 3 — which courts, when, and the turnover gap. */}
              <div style={SUBSECTION}>
                <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.35rem' }}>Courts for this package</label>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {allCourts.map((court) => {
                    const num = Number(court.number);
                    const picked = (pkg.courts || []).map(Number).includes(num);
                    return (
                      <button
                        key={court.id || num}
                        type="button"
                        onClick={() => toggleCourt(pkg.id, num)}
                        aria-pressed={picked}
                        style={{
                          padding: '0.25rem 0.6rem', borderRadius: '999px', cursor: 'pointer', fontSize: '0.72rem',
                          border: `1px solid ${picked ? COLORS.cyanDeep : COLORS.line}`,
                          background: picked ? COLORS.cyanDeep : 'transparent',
                          color: picked ? '#ffffff' : COLORS.textSecondary,
                        }}
                      >
                        {(court.name || '').trim() || `Court ${num}`}
                      </button>
                    );
                  })}
                  {allCourts.length === 0 && (
                    <span style={{ fontSize: '0.74rem', opacity: 0.6 }}>No courts configured yet — add them in Config → Sports &amp; Courts.</span>
                  )}
                </div>

                <div style={{ ...ROW, marginTop: '0.6rem', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.66rem' }}>Available from (24h)</label>
                    <input
                      type="number" min="0" max="23"
                      value={pkg.startHour ?? ''}
                      onChange={(e) => updatePackage(pkg.id, { startHour: parseInt(e.target.value) || 0 })}
                      style={{ width: '80px' }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.66rem' }}>Available to (24h)</label>
                    <input
                      type="number" min="0" max="23"
                      value={pkg.endHour ?? ''}
                      onChange={(e) => updatePackage(pkg.id, { endHour: parseInt(e.target.value) || 0 })}
                      style={{ width: '80px' }}
                    />
                  </div>
                  {/* "15 to 0" is not readable as 3PM-12AM; spell it out,
                      including the wrap, the way the rate-band editor does. */}
                  <span style={{ fontSize: '0.72rem', opacity: 0.75, paddingBottom: '0.4rem' }}>
                    {describePackageWindow(pkg)}
                    {Number(pkg.endHour) <= Number(pkg.startHour) ? ' — ends next day' : ''}
                  </span>
                  <div className="form-group" style={{ margin: 0, marginLeft: 'auto' }}>
                    <label style={{ fontSize: '0.66rem' }}>Cleanup allowance (min)</label>
                    <input
                      type="number" min="0"
                      value={pkg.bufferMinutes ?? ''}
                      placeholder={String(Number(draft.packageBufferMinutes) || 0)}
                      onChange={(e) => updatePackage(pkg.id, { bufferMinutes: e.target.value === '' ? null : (parseInt(e.target.value) || 0) })}
                      style={{ width: '110px' }}
                    />
                    <small style={{ display: 'block', marginTop: '0.2rem', opacity: 0.6, fontSize: '0.66rem' }}>
                      Blank = default ({resolvedBuffer} min)
                    </small>
                  </div>
                </div>
                <p style={{ margin: '0.45rem 0 0', fontSize: '0.7rem', opacity: 0.6 }}>
                  Guests pick a start time anywhere in this {windowHours}-hour window, as long as the package
                  finishes before it closes.
                </p>
              </div>

              {/* Row 4 — what's included. Plain lines; guests see them as a list. */}
              <div style={SUBSECTION}>
                <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.35rem' }}>Inclusions</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {(pkg.inclusions || []).map((inc) => (
                    <div key={inc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <input
                        type="text"
                        value={inc.text || ''}
                        onChange={(e) => updateInclusion(pkg.id, inc.id, e.target.value)}
                        placeholder="e.g. Free use of tables and chairs"
                        style={{ flex: 1 }}
                      />
                      <button
                        onClick={() => removeInclusion(pkg.id, inc.id)}
                        style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '0.8rem' }}
                        aria-label="Remove inclusion"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => addInclusion(pkg.id)}
                  style={{ marginTop: '0.4rem', padding: '0.35rem 0.7rem', background: 'none', border: `1px dashed ${COLORS.line}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.74rem', color: COLORS.textSecondary }}
                >
                  + Add inclusion
                </button>
              </div>

              {/* Config problems that would otherwise only surface as a guest
                  being unable to book. Errors block, warnings don't. */}
              {issues.map((issue, idx) => (
                <p
                  key={`${issue.code}-${idx}`}
                  style={{
                    margin: '0.1rem 0 0', fontSize: '0.72rem', lineHeight: 1.45,
                    color: issue.level === ISSUE_LEVELS.ERROR ? '#c0392b' : COLORS.warning,
                    fontWeight: issue.level === ISSUE_LEVELS.ERROR ? 600 : 400,
                  }}
                >
                  {issue.level === ISSUE_LEVELS.ERROR ? '⚠ ' : ''}{issue.message}
                </p>
              ))}
            </div>
          );
        })}
        {packages.length === 0 && (
          <p style={{ opacity: 0.6, fontSize: '0.82rem' }}>No packages yet. Add one below.</p>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button
          onClick={handleAddPackage}
          style={{ padding: '0.45rem 0.8rem', background: 'none', border: `1px dashed ${COLORS.line}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', color: COLORS.textSecondary }}
        >
          + Add package
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          style={{
            padding: '0.5rem 1.2rem', background: COLORS.neonLime, color: COLORS.deepBg,
            border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.82rem',
            cursor: (saving || !dirty) ? 'not-allowed' : 'pointer', opacity: (saving || !dirty) ? 0.55 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save Packages'}
        </button>
        {dirty && !saving && (
          <span style={{ fontSize: '0.75rem', color: COLORS.warning }}>Unsaved changes</span>
        )}
      </div>
    </div>
  );
}

export default AdminPackages;
